import { createActionGroup, emptyProps, props } from '@ngrx/store';

import * as fromGenerated from '../../_generated';

export type AdminTeammatesSortColumn = 'name' | 'skills' | 'groups' | 'users';
export type AdminTeammatesSortDirection = 'asc' | 'desc';

export type AdminTeammatesAccessMode = 'invited-only' | 'org-wide';
export type AvatarPart = 'color' | 'shape' | 'eyes';
export type TeammateTranslationFieldKey =
  | 'name'
  | 'title'
  | 'welcomeMessage'
  | 'purposeMessage';
export type TeammateBuilderLocale = 'en' | 'fr';

/** A staged invitee on the Builder's General Access panel. */
export interface AdminTeammateInvitee {
  kind: 'user' | 'group';
  id: string;
  displayName: string;
  /** email (users) or "Groupe · N membres" (groups) — for the chip's second line. */
  secondary?: string | undefined;
}

/** Create body shape (matches the generated CreateShareableTeammateDto). */
export interface CreateTeammateBody {
  defaultLocale: fromGenerated.CreateShareableTeammateDto.DefaultLocaleEnum;
  color: fromGenerated.CreateShareableTeammateDto.ColorEnum;
  shape: fromGenerated.CreateShareableTeammateDto.ShapeEnum;
  eyes: fromGenerated.CreateShareableTeammateDto.EyesEnum;
  translations: fromGenerated.CreateTeammateTranslationDto[];
  attachedSkillIds: string[];
}

export const AdminTeammatesActions = createActionGroup({
  source: 'AdminTeammates',
  events: {
    'Load Teammates': emptyProps(),
    'Load Teammates Success': props<{
      teammates: fromGenerated.AdminTeammateView[];
    }>(),
    'Load Teammates Failure': props<{ error: string }>(),

    'Set Sort': props<{
      column: AdminTeammatesSortColumn | null;
      direction: AdminTeammatesSortDirection;
    }>(),
    'Set Filter': props<{ filter: string }>(),

    // Lazy per-row expand payload for the Skills / Groups / Users count cells.
    'Load Dropdown Payload': props<{ teammateId: string }>(),
    'Load Dropdown Payload Success': props<{
      teammateId: string;
      payload: fromGenerated.TeammateDropdownPayloadView;
    }>(),
    'Load Dropdown Payload Failure': props<{
      teammateId: string;
      error: string;
    }>(),

    // ─── Builder data loads (fired on Builder open) ──────────────────────────
    'Load All Users': emptyProps(),
    'Load All Users Success': props<{ users: fromGenerated.AdminUserView[] }>(),
    'Load All Users Failure': props<{ error: string }>(),
    'Load All Groups': emptyProps(),
    // The 'all' (org-wide) group id is derived from this list in the reducer.
    'Load All Groups Success': props<{
      groups: fromGenerated.GroupView[];
    }>(),
    'Load All Groups Failure': props<{ error: string }>(),

    // ─── Overlay lifecycle ───────────────────────────────────────────────────
    'Open Create Modal': emptyProps(),
    'Open Edit Modal': props<{ teammate: fromGenerated.AdminTeammateView }>(),
    // Seeds the edit Builder's staged invitees (chained off Open Edit Modal).
    'Seed Edit Invitees': props<{ invitees: AdminTeammateInvitee[] }>(),
    // Seeds the edit Builder's attached skills (from the dropdown payload).
    'Seed Edit Skills': props<{ skillIds: string[] }>(),
    'Open Delete Confirm': props<{
      teammate: fromGenerated.AdminTeammateView;
    }>(),
    'Close Delete Confirm': emptyProps(),
    'Close Modal': emptyProps(),

    // ─── Staged-data mutations (Builder edits) ───────────────────────────────
    'Set Avatar Part': props<{ part: AvatarPart; value: string }>(),
    'Set Translation Field': props<{
      field: TeammateTranslationFieldKey;
      locale: TeammateBuilderLocale;
      value: string;
    }>(),
    'Set Access Mode': props<{ mode: AdminTeammatesAccessMode }>(),
    'Add Invitee': props<{ invitee: AdminTeammateInvitee }>(),
    'Remove Invitee': props<{ id: string; kind: 'user' | 'group' }>(),
    'Add Attached Skill': props<{ skillId: string }>(),
    'Remove Attached Skill': props<{ skillId: string }>(),

    // ─── Skills typeahead ────────────────────────────────────────────────────
    'Search Skills': props<{ query: string }>(),
    'Search Skills Success': props<{
      results: fromGenerated.AdminSkillView[];
    }>(),
    'Search Skills Failure': props<{ error: string }>(),

    // ─── Create / Update / Delete ────────────────────────────────────────────
    'Create Teammate': props<{
      body: CreateTeammateBody;
      accessMode: AdminTeammatesAccessMode;
      invitees: AdminTeammateInvitee[];
      allGroupId: string | null;
    }>(),
    'Create Teammate Success': props<{
      teammate: fromGenerated.AdminTeammateView;
      accessMode: AdminTeammatesAccessMode;
      invitees: AdminTeammateInvitee[];
      allGroupId: string | null;
    }>(),
    'Create Teammate Failure': props<{ error: string }>(),

    'Update Teammate Content': props<{
      teammateId: string;
      body: fromGenerated.UpdateTeammateDto;
    }>(),
    'Update Teammate Content Success': props<{
      teammate: fromGenerated.AdminTeammateView;
    }>(),
    'Update Teammate Content Failure': props<{
      teammateId: string;
      error: string;
    }>(),

    'Delete Teammate': props<{ teammateId: string }>(),
    'Delete Teammate Success': props<{ teammateId: string }>(),
    'Delete Teammate Failure': props<{ teammateId: string; error: string }>(),

    // ─── Invitee fan-out (one leaf per add/remove) ───────────────────────────
    'Fan Out Invitee Diff': props<{
      teammateId: string;
      adds: AdminTeammateInvitee[];
      removes: AdminTeammateInvitee[];
      allGroupId: string | null;
    }>(),

    'Grant User Teammate Access': props<{
      teammateId: string;
      userId: string;
      userDisplayName: string;
    }>(),
    'Grant User Teammate Access Success': props<{
      teammateId: string;
      userId: string;
    }>(),
    'Grant User Teammate Access Failure': props<{
      teammateId: string;
      userId: string;
      userDisplayName: string;
      error: string;
    }>(),

    'Revoke User Teammate Access': props<{
      teammateId: string;
      userId: string;
      userDisplayName: string;
    }>(),
    'Revoke User Teammate Access Success': props<{
      teammateId: string;
      userId: string;
    }>(),
    'Revoke User Teammate Access Failure': props<{
      teammateId: string;
      userId: string;
      userDisplayName: string;
      error: string;
    }>(),

    'Grant Group Teammate Access': props<{
      teammateId: string;
      groupId: string;
      groupDisplayName: string;
    }>(),
    'Grant Group Teammate Access Success': props<{
      teammateId: string;
      groupId: string;
    }>(),
    'Grant Group Teammate Access Failure': props<{
      teammateId: string;
      groupId: string;
      groupDisplayName: string;
      error: string;
    }>(),

    'Revoke Group Teammate Access': props<{
      teammateId: string;
      groupId: string;
      groupDisplayName: string;
    }>(),
    'Revoke Group Teammate Access Success': props<{
      teammateId: string;
      groupId: string;
    }>(),
    'Revoke Group Teammate Access Failure': props<{
      teammateId: string;
      groupId: string;
      groupDisplayName: string;
      error: string;
    }>(),
  },
});
